/* eslint-disable */
import { useContext, useEffect, useState } from "react";

import { skipToken } from "@reduxjs/toolkit/query";
import { saveAs } from "file-saver";

import { DocumentEntityRequest } from "../../models/request/documentEntityRequest";
import { DocumentRequest, DownloadDocumentRequest } from "../../models/request/documentRequest";
import { DocumentText, DownloadIcon } from "../../common/icons";
import { LocalizationContext } from "../../App";
import { fileExtensionRegex } from "../../common/constants";
import { useDownloadPolicyDocumentQuery, useGetPoliciesDocumentsMutation } from "../../store/policiesSlice";

interface DocumentListProps {
  entityType: string;
  entityIdentifier: string;
}

export const DocumentList = ({ entityType, entityIdentifier }: DocumentListProps) => {
  const l10n = useContext(LocalizationContext);
  const [getMultipleDocuments, { data: documents, isLoading: isLoadingDocuments }] =
    useGetPoliciesDocumentsMutation();
  const [downloadDocumentRequest, setDownloadDocumentRequest] = useState<
    DownloadDocumentRequest | undefined
  >(undefined);
  const { data: downloadedDocument, originalArgs: downloadDocumentArgs } = useDownloadPolicyDocumentQuery(
    downloadDocumentRequest ?? skipToken,
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    const docEntityRequestArray: DocumentEntityRequest[] = [
      {
        entityType,
        entityIdentifier,
      },
    ];

    const documentRequest: DocumentRequest = {
      documents: docEntityRequestArray,
    };

    getMultipleDocuments(documentRequest);
  }, []);

  useEffect(() => {
    if (downloadedDocument) {
      try {
        saveAs(
          downloadedDocument as Blob,
          downloadDocumentArgs?.documentName || "downloaded_document.pdf"
        );
        setDownloadDocumentRequest(undefined);
      } catch (error) {
        // TODO: Add error handling UI
        console.error("Error downloading the document:", error);
      }
    }
  }, [downloadedDocument]);

  const downloadDocument = async (
    entityType: string,
    entityIdentifier: string,
    documentId: string,
    documentName: string,
    documentExtension?: string
  ) => {
    setDownloadDocumentRequest({
      entityType,
      entityIdentifier,
      documentId,
      documentName: documentName.match(fileExtensionRegex)
        ? documentName
        : [documentName || "downloaded_document", documentExtension].join("."),
    });
  };

  if (isLoadingDocuments) {
    return (
      <div className="flex justify-center items-center">
        <span>{l10n.common.loading}</span>
      </div>
    );
  }

  const visibleDocs = documents?.filter((document) => document.currentVersion.clientVisible) ?? [];

  return (
    <>
      {visibleDocs?.length > 0 ? (
        <div className="overflow-x-auto">
          <div className="overflow-hidden">
            {visibleDocs.map((document) => (
              <div
                className="flex gap-2 justify-between p-1.5"
                key={document.id}
              >
                <div className="flex gap-1 items-center overflow-hidden">
                  <DocumentText className="w-5 h-5 shrink-0 stroke-1.5 stroke-card" />
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                    {document.currentVersion.name
                      .replace(/_/g, " ")
                      .replace("Beraterinformationen.pdf", "Beraterinformationen VAG45.pdf")}{" "}
                    {/* TODO:Maybe go with product name renaming. */}
                  </span>
                </div>
                {downloadDocumentRequest?.documentId ===
                document.id.toString() ? (
                  <div className="w-6 ellipsis text-card">&nbsp;</div>
                ) : (
                  <button
                    onClick={() =>
                      downloadDocument(
                        "1",
                        entityIdentifier,
                        document.id.toString(),
                        document.currentVersion.name,
                        document.currentVersion.extension
                      )
                    }
                  >
                    <DownloadIcon className="w-5 h-5 shrink-0 stroke-1.5 stroke-card" />
                  </button>
                )}
              </div>
            ))}
            <div className="flex gap-2 justify-between p-1.5" key="datenschutz">
              <div className="flex gap-1 items-center overflow-hidden">
                <DocumentText className="w-5 h-5 shrink-0 stroke-1.5 stroke-card" />
                <span className="leading-none overflow-hidden text-ellipsis whitespace-nowrap">
                  <span
                    dangerouslySetInnerHTML={{
                      __html: l10n.label.dataprotectionprovisionslink,
                    }}
                  ></span>
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center text-sm text-gray-900 px-4 py-4">
          {l10n.policy.noDocuments}
        </div>
      )}
    </>
  );
};
